import { getAngularProvider, isAngular } from 'services/utils/angularUtils';
import { createLeftMenuData } from 'components/layout/leftMenu/consts';
import demandConfig from 'services/metaData/demandConfig';
import generalConfig from 'services/metaData/generalConfig';
import userDataService from './userData';
import http from './utils/http';

const keyName = 'authServiceDB';
const localStorageKeysToRemove = ['authServiceDB', 'campaignSize', 'tempCampaign', 'ssoData'];

class Auth {
    // eslint-disable-next-line class-methods-use-this
    async isAuthorized(permissions) {
        if (!permissions) {
            return true;
        }

        const userData = await (userDataService.getUserData().catch(() => ({})));

        const userPermissions = userData.permissions;

        if (!userPermissions) {
            return false;
        }

        let authorized = true;
        const numberOfPermissions = permissions.length;

        for (let i = 0; i < numberOfPermissions; i++) {
            const permission = permissions[i];
            const notPermission = permission[0] === '!' ? permission.slice(1) : false;
            const orPermissions = permission.indexOf(' or ') > -1 ? permission.split(' or ') : [];
            if (notPermission && userPermissions[notPermission]) {
                // permission exists in the list but was requested not to be
                authorized = false;
            } else if (orPermissions.length > 0) {
                // if permission contains 'or' string -
                // check if the user has at least one of the permissions
                let found = false;
                for (let j = 0; j < orPermissions.length; j++) {
                    if (userPermissions[orPermissions[j]]) {
                        found = true;
                        break;
                    }
                }
                if (!found) {
                    authorized = false;
                }
            } else if ((notPermission === false) && !userPermissions[permission]) {
                // if the permissions is not requested to be excluded, check if the user has it

                // permission does not exist in the list or is not granted to that user
                authorized = false;
            }
        }

        return authorized;
    }

    loginWithToken(token) {
        this.setToken(token);
    }

    login(username, password) {
        const apiUrl = `${generalConfig().sspPlatformJsDomain}/partners/auth/v2/login`;
        return http.post(apiUrl, {
            username,
            password
        }, false).then((response) => {
            this.setToken(response.data);
        });
    }

    // eslint-disable-next-line class-methods-use-this
    logout() {
        window.sspToken = null;

        if (window.mixpanel && window.mixpanel.cookie) {
            window.mixpanel.cookie.clear();
        }

        localStorageKeysToRemove.forEach((key) => localStorage.removeItem(key));

        userDataService.clearUserData();
        demandConfig.clearLoginAs();

        if (isAngular()) {
            getAngularProvider('authService')
                .logout();
        }
    }

    requestResetPassword(username, captchaResponse) {
        return http.post('/partners/auth/forgot', {
            username,
            captchaResponse: captchaResponse || null
        }, false);
    }

    // eslint-disable-next-line class-methods-use-this
    setToken(token) {
        localStorage.setItem(keyName, JSON.stringify(token));
        if (!window.sspToken) {
            window.sspToken = {};
        }
        window.sspToken.tokenData = token;
    }

    isLoggedIn() {
        let data = localStorage.getItem(keyName);
        if (!data) return false;

        try {
            data = JSON.parse(localStorage.getItem(keyName));

            return data.token && data.expiration && !this.isExpired(data.expiration);
        } catch {
            return false;
        }
    }

    // eslint-disable-next-line class-methods-use-this
    isExpired(dateStr) {
        const dateParts = dateStr.split('-');
        const expiration = new Date(
            Date.UTC(dateParts[0], dateParts[1] - 1, dateParts[2], 23, 59, 0)
        );
        const now = new Date();

        return expiration < now;
    }

    isCurrentPage(page, id) {
        return page.id === id ? page : null;
    }

    async isPageAuthorized(pageId) {
        let foundPage = null;
        const leftMenuData = createLeftMenuData();
        for (let sectionIndex = 0; sectionIndex < leftMenuData.length && !foundPage; sectionIndex++) {
            const section = leftMenuData[sectionIndex];
            foundPage = this.isCurrentPage(section, pageId);
            if (section.pageGroups) {
                for (let pageGroupIndex = 0;
                    pageGroupIndex < section.pageGroups.length && !foundPage;
                    pageGroupIndex++) {
                    const pageGroup = section.pageGroups[pageGroupIndex];
                    foundPage = this.isCurrentPage(pageGroup, pageId);

                    if (pageGroup.pages) {
                        for (let pageIndex = 0; pageIndex < pageGroup.pages.length && !foundPage; pageIndex++) {
                            foundPage = this.isCurrentPage(pageGroup.pages[pageIndex], pageId);
                        }
                    }
                }
            }
        }
        if (!foundPage || !foundPage.permissions) {
            return true;
        }
        return this.isAuthorized(foundPage.permissions);
    }

    async authorizeSubPages(pages, listsOrder) {
        if (pages && pages.length) {
            // filter current level by permission
            let authorizedPages = await this.getFilteredPagesByPermissions(pages.slice(0));
            if (listsOrder.length > 0) {
                const subPagesProp = listsOrder.splice(0, 1);
                // step into next level of pages - authorize sub pages
                authorizedPages = await Promise.all(authorizedPages.map(async (page) => ({
                    ...page,
                    [subPagesProp]:
                        await this.authorizeSubPages(page[subPagesProp], listsOrder.slice(0))
                })));
            }
            return authorizedPages;
        }
        return pages;
    }

    async getFilteredPagesByPermissions(list) {
        const authorizeMap = {};
        await Promise.all(list.map(async (item) => {
            authorizeMap[item.id] = await this.isAuthorized(item.permissions);
            return authorizeMap[item.id];
        }));
        const formattedList = await Promise.all(list.map(async (item) => ({
            ...item,
            inlineLinkAuthorized: await this.isAuthorized(item.inlineLinkPermissions)
        })));
        return formattedList.filter((item) => authorizeMap[item.id]);
    }

    async getAuthorizedPages(sections) {
        const pageListsProps = ['pageGroups', 'pages'];
        return this.authorizeSubPages(sections, pageListsProps);
    }
}

const instance = new Auth();

if (!window.SSPServices) {
    window.SSPServices = {};
}
window.SSPServices.authService = instance;

export default instance;
